<template>
  <Transition name="message-fade">
    <div v-if="isVisible" class="message-popup" :class="type">
      {{ message }}
    </div>
  </Transition>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';

export default defineComponent({
  name: 'MessagePopup',
  props: {
    message: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'info',
      validator: (value: string) => ['info', 'success', 'warning', 'error'].includes(value)
    },
    duration: {
      type: Number,
      default: 3000
    },
    position: {
      type: String,
      default: 'top-right',
      validator: (value: string) => ['top-right', 'top-left', 'bottom-right', 'bottom-left'].includes(value)
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const isVisible = ref(false);
    let timeoutId: number | null = null;

    const show = () => {
      isVisible.value = true;
      
      if (props.duration > 0) {
        timeoutId = window.setTimeout(() => {
          hide();
        }, props.duration);
      }
    };

    const hide = () => {
      isVisible.value = false;
      emit('close');
    };

    const clearTimeout = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    onMounted(() => {
      show();
    });

    onUnmounted(() => {
      clearTimeout();
    });

    return {
      isVisible,
      hide
    };
  }
});
</script>

<style scoped>
.message-popup {
  position: fixed;
  padding: 12px 16px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  z-index: 10000;
  font-size: 14px;
  max-width: 300px;
  word-wrap: break-word;
  border-left: 4px solid;
  animation: slideIn 0.3s ease-out;
}

.message-popup.top-right {
  top: 20px;
  right: 20px;
}

.message-popup.top-left {
  top: 20px;
  left: 20px;
}

.message-popup.bottom-right {
  bottom: 20px;
  right: 20px;
}

.message-popup.bottom-left {
  bottom: 20px;
  left: 20px;
}

.message-popup.info {
  background: #E3F2FD;
  color: #1565C0;
  border-left-color: #2196F3;
}

.message-popup.success {
  background: #E8F5E8;
  color: #2E7D32;
  border-left-color: #4CAF50;
}

.message-popup.warning {
  background: #FFF3E0;
  color: #EF6C00;
  border-left-color: #FF9800;
}

.message-popup.error {
  background: #FFEBEE;
  color: #C62828;
  border-left-color: #F44336;
}

/* Custom KEP message style */
.message-popup.kep {
  background: #FFE4B5;
  color: #8B4513;
  border-left-color: #FFA500;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.message-fade-enter-active,
.message-fade-leave-active {
  transition: all 0.3s ease;
}

.message-fade-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.message-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
